import {
	FormControl,
	InputLabel,
	OutlinedInput,
	Select,
	Stack,
	StackProps,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import _ from "lodash";

import { Option } from "~ui-components/components/atoms/option";
import {
	PolygonSimpleConfigs,
	SymbolStyle,
} from "~ui-components/types/__generated/gql/graphql";

type Props = StackProps & {
	value: PolygonSimpleConfigs;
	onChange: (next: PolygonSimpleConfigs) => void;
};

export const PolygonStyle = (props: Props) => {
	const { value, onChange, ...stackProps } = props;

	const fill = value.symbolStyle?.fill ?? {};
	const stroke = value.symbolStyle?.stroke ?? {};

	const handleSymbolChange = <
		K extends keyof SymbolStyle,
		P extends keyof NonNullable<NonNullable<SymbolStyle[K]>>,
	>(
		section: K,
		prop: P,
		val: NonNullable<NonNullable<SymbolStyle[K]>[P]>,
	) => {
		const currentSymbolStyle = value.symbolStyle ?? {};
		const currentSection = (currentSymbolStyle[section] ?? {}) as Record<
			string,
			any
		>;

		onChange({
			...value,
			symbolStyle: {
				...currentSymbolStyle,
				[section]: {
					...currentSection,
					[prop]: val,
				},
			},
		});
	};

	return (
		<Stack
			spacing={3}
			{...stackProps}>
			<InputLabel>Fill and Stroke</InputLabel>

			{/* Fill color */}
			<FormControl sx={{ width: 200 }}>
				<InputLabel>Fill colour</InputLabel>
				<MuiColorInput
					format="hex"
					value={fill.color ?? "#ffffff"}
					onChange={(color) => handleSymbolChange("fill", "color", color)}
				/>
			</FormControl>

			<FormControl sx={{ width: 200 }}>
				<InputLabel>Fill style</InputLabel>
				<Select
					value={fill.style ?? "solid"}
					onChange={(e) => handleSymbolChange("fill", "style", e.target.value)}>
					<Option value="solid">Solid</Option>
					<Option value="verticalline">Vertica Lline</Option>
					<Option value="horizontalline">Horizontal Line</Option>
					<Option value="slash">Slash</Option>
					<Option value="backslash">Back Slash</Option>
					<Option value="grid">Grid</Option>
					<Option value="diagonalgrid">Diagonal Grid</Option>
					<Option value="none">None</Option>
				</Select>
			</FormControl>

			{/* Fill opacity */}
			<FormControl sx={{ width: 200 }}>
				<InputLabel>Fill Opacity</InputLabel>
				<Select
					value={fill.opacity ?? 1}
					onChange={(e) =>
						handleSymbolChange(
							"fill",
							"opacity",
							parseFloat(String(e.target.value)),
						)
					}>
					{_.range(0, 11).map((value) => (
						<Option
							key={value}
							value={value / 10}>
							{value * 10}%
						</Option>
					))}
				</Select>
			</FormControl>

			{/* Stroke color */}
			<FormControl sx={{ width: 200 }}>
				<InputLabel>Stroke colour</InputLabel>
				<MuiColorInput
					format="hex"
					value={stroke.color ?? "#000000"}
					onChange={(color) => handleSymbolChange("stroke", "color", color)}
				/>
			</FormControl>

			{/* Stroke width */}
			<FormControl sx={{ width: 200 }}>
				<InputLabel>Stroke width</InputLabel>
				<OutlinedInput
					type="number"
					value={stroke.width ?? 1}
					onChange={(e) =>
						handleSymbolChange(
							"stroke",
							"width",
							parseFloat(String(e.target.value)),
						)
					}
				/>
			</FormControl>

			{/* Stroke opacity */}
			<FormControl sx={{ width: 200 }}>
				<InputLabel>Stroke opacity</InputLabel>
				<Select
					value={stroke.opacity ?? 1}
					onChange={(e) =>
						handleSymbolChange(
							"stroke",
							"opacity",
							parseFloat(String(e.target.value)),
						)
					}>
					{_.range(0, 11).map((value) => (
						<Option
							key={value}
							value={value / 10}>
							{value * 10}%
						</Option>
					))}
				</Select>
			</FormControl>

			{/* Stroke style */}
			<FormControl sx={{ width: 200 }}>
				<InputLabel>Stroke style</InputLabel>
				<Select
					value={stroke.style ?? "solid"}
					onChange={(e) =>
						handleSymbolChange(
							"stroke",
							"style",
							e.target.value as "none" | "solid" | "dash" | "dot",
						)
					}>
					<Option value="none">None</Option>
					<Option value="solid">Solid</Option>
					<Option value="dash">Dash</Option>
					<Option value="dot">Dot</Option>
				</Select>
			</FormControl>
		</Stack>
	);
};
